import React, { SetStateAction, useEffect, useState } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import KeyPerformanceIndicators from "../../components/charts/KeyPerformanceIndicators.chart";
import {
  CumulativeUsersOverTime,
  StatieItems,
  PrimaryButton,
  RedButton,
} from "../../components";
import { VolumesGeneralFilters } from "../Volumes/VolumesGeneralFilters";
import { useMarketPricing } from "../../store/useMarketPricing";
import { Materials } from "../../components/charts/Materials.chart";
import axios from "axios";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useAlert } from "../../store/useAlert";

dayjs.extend(isoWeek);

const SummaryScreen = () => {
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const { setAlert } = useAlert();
  const [filterType, setFilterType] = useState<"location" | "city">("location");
  const [filterPlaces, setFilterPlaces] = useState<string[]>([]);
  const [sequenceStep, setSequenceStep] = useState(0);

  const [exportFromYear, setExportFromYear] = useState(dayjs().year());
  const [exportFromWeek, setExportFromWeek] = useState(dayjs().isoWeek() - 1);
  const [exportToYear, setExportToYear] = useState(dayjs().year());
  const [exportToWeek, setExportToWeek] = useState(dayjs().isoWeek());
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  // Only Materials (index 0) and StatieItems (index 1) are sequenced on this page now,
  // so the last one advances the step to 2. (ParcelItems/RevenueMargins/ParcelRevenuePerCourier
  // were removed from the summary page — bump this if sequenced charts are added/removed.)
  const areChartsReady = sequenceStep >= 2;

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSequenceStep(0);
  }, [filterType, filterPlaces]);

  const setFilterTypeWithReset = (v: SetStateAction<"location" | "city">) => {
    setSequenceStep(0);
    setFilterType(v);
  };

  const handleExportClick = () => {
    if (!areChartsReady) {
      setAlert({
        isOpen: true,
        message: "Please wait for all charts to finish loading before export.",
        severity: "warning",
      });
      return;
    }
    setIsExportModalOpen(true);
  };

  const handleExport = async () => {
    try {
      if (
        exportFromYear > exportToYear ||
        (exportFromYear === exportToYear && exportFromWeek > exportToWeek)
      ) {
        setAlert({
          isOpen: true,
          message:
            "Invalid range: 'From' week must be before or equal to 'To' week.",
          severity: "error",
        });
        return;
      }

      setIsExportModalOpen(false);
      setIsExporting(true);
      const response = await axios.get("/summary/export/weekly", {
        params: {
          fromYear: exportFromYear,
          fromWeek: exportFromWeek,
          toYear: exportToYear,
          toWeek: exportToWeek,
        },
      });

      if (!response?.data?.success || !response?.data?.data) {
        throw new Error(
          response?.data?.message ||
            "Export data is unavailable. Please try again."
        );
      }

      const { data } = response.data;
      const {
        volumesByDateAndStoreAndMaterial,
        tomraItemsPerStorePerDay,
        allMaterials,
        materialMeasurements,
        allDates,
        droppersByDateAndStore,
        regularDroppersByDateAndStore,
        statieDroppersByDateAndStore,
        statieRegularDroppersByDateAndStore,
        totalDownloadsPerStore,
      } = data;

      // Drop the columns the client flagged as not needed ("Nee"): Flattened Cans,
      // Laptops / flatscreens, Smartphones / Tablets, and both Statiegeld materials.
      // Matched as case-insensitive substrings so minor label variations still match;
      // none of the kept materials (cooking_oils_fats, e_waste, Koffiecups,
      // plastic_verpakkingen, textiles) contain these tokens, so there is no over-removal.
      const EXCLUDED_MATERIAL_TOKENS = [
        "flattened",
        "laptop",
        "flatscreen",
        "smartphone",
        "tablet",
        "statiegeld",
      ];
      const exportMaterials = ((allMaterials as string[]) || []).filter((m) => {
        const normalized = String(m).toLowerCase();
        return !EXCLUDED_MATERIAL_TOKENS.some((token) =>
          normalized.includes(token)
        );
      });

      // The Unit column has to say how each material is actually measured:
      // kg-based streams are weights, but unit-based ones (Books, Bike, Ink
      // Cartridges, ...) are item counts, not kilograms. The export endpoint
      // reports the measurement it aggregated each material under, which is
      // the authoritative answer; older backends don't send it, so fall back
      // to the market pricing list this page already loads on mount. A
      // material neither source knows keeps the previous "kg" default rather
      // than dropping out of the export.
      const normalizeMaterialKey = (value: string) =>
        String(value ?? "").trim().toLowerCase();
      const measurementByMaterial = new Map<string, string>();
      let pricings = marketPrices;
      if (!pricings?.length) {
        const pricingResponse = await axios.get("drops/market");
        pricings = Array.isArray(pricingResponse?.data)
          ? pricingResponse.data
          : [];
      }
      (pricings ?? []).forEach((pricing) => {
        if (pricing?.measurement) {
          measurementByMaterial.set(
            normalizeMaterialKey(pricing?.name),
            pricing.measurement
          );
        }
      });
      Object.entries(
        (materialMeasurements as Record<string, string>) || {}
      ).forEach(([material, measurement]) => {
        if (measurement) {
          measurementByMaterial.set(normalizeMaterialKey(material), measurement);
        }
      });

      const delimiter = ";";
      const escapeCsvValue = (value: string | number) => {
        const stringValue = String(value ?? "");
        if (
          stringValue.includes('"') ||
          stringValue.includes("\n") ||
          stringValue.includes(delimiter)
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Get all stores
      const allStoresSet = new Set<string>();
      Object.values(volumesByDateAndStoreAndMaterial).forEach(
        (dateData: any) => {
          Object.keys(dateData).forEach((store) => allStoresSet.add(store));
        }
      );
      Object.values(tomraItemsPerStorePerDay || {}).forEach((dateData: any) => {
        Object.keys(dateData).forEach((store) => allStoresSet.add(store));
      });
      Object.values(droppersByDateAndStore || {}).forEach((byStore: any) => {
        Object.keys(byStore || {}).forEach((store) => allStoresSet.add(store));
      });
      Object.values(regularDroppersByDateAndStore || {}).forEach(
        (byStore: any) => {
          Object.keys(byStore || {}).forEach((store) =>
            allStoresSet.add(store)
          );
        }
      );
      Object.values(statieDroppersByDateAndStore || {}).forEach(
        (byStore: any) => {
          Object.keys(byStore || {}).forEach((store) =>
            allStoresSet.add(store)
          );
        }
      );
      Object.values(statieRegularDroppersByDateAndStore || {}).forEach(
        (byStore: any) => {
          Object.keys(byStore || {}).forEach((store) =>
            allStoresSet.add(store)
          );
        }
      );
      Object.keys(totalDownloadsPerStore || {}).forEach((store) =>
        allStoresSet.add(store)
      );
      const stores = Array.from(allStoresSet).sort();
      const sortedDates = [...allDates].sort();

      // Long/tidy format instead of one column per material: a row per
      // (date, store, stream) triple, so a new material or metric is a new
      // row, not a new column the export code has to know about. Every
      // stream that used to be its own wide column - materials, Tomra items,
      // the dropper counts, downloads - is written the same way here, each
      // with the unit it's actually measured in.
      const header = ["Date", "Store", "Material", "Weight", "Unit"];
      const rows: string[] = [header.map(escapeCsvValue).join(delimiter)];
      const pushRow = (
        date: string,
        store: string,
        material: string,
        value: number,
        unit: string,
        decimals: number
      ) => {
        // Zero-value streams are skipped rather than written as "0.00" - with
        // one row per stream there's no fixed column grid to keep aligned, so
        // dropping empty ones is what actually makes this "more readable".
        if (!(value > 0)) {
          return;
        }
        rows.push(
          [date, store, material, value.toFixed(decimals), unit]
            .map(escapeCsvValue)
            .join(delimiter)
        );
      };

      // totalDownloadsPerStore has no daily breakdown - it's one cumulative
      // count per store for the whole selected range - so it's anchored to
      // the first date each store appears on, same as the wide export used
      // to do by leaving the other date's cell blank for that column.
      const storeDownloadsWritten = new Set<string>();

      sortedDates.forEach((date: string) => {
        stores.forEach((store) => {
          exportMaterials.forEach((material: string) => {
            const weight =
              volumesByDateAndStoreAndMaterial[date]?.[store]?.[material] || 0;
            const isItemBased =
              measurementByMaterial.get(normalizeMaterialKey(material)) ===
              "unit";
            // Item counts belong in the column as whole numbers. A fractional
            // value on an item-based material means kg leaked into the sum
            // upstream, so keep those decimals visible instead of rounding
            // them into a count that looks legitimate.
            const decimals =
              isItemBased && Number.isInteger(weight) ? 0 : 2;
            pushRow(
              date,
              store,
              material,
              weight,
              isItemBased ? "items" : "kg",
              decimals
            );
          });

          pushRow(
            date,
            store,
            "Tomra Items",
            tomraItemsPerStorePerDay?.[date]?.[store] || 0,
            "items",
            0
          );
          pushRow(
            date,
            store,
            "Droppers",
            droppersByDateAndStore?.[date]?.[store] ?? 0,
            "count",
            0
          );
          pushRow(
            date,
            store,
            "Regular Droppers",
            regularDroppersByDateAndStore?.[date]?.[store] ?? 0,
            "count",
            0
          );
          pushRow(
            date,
            store,
            "Statie Droppers",
            statieDroppersByDateAndStore?.[date]?.[store] ?? 0,
            "count",
            0
          );
          pushRow(
            date,
            store,
            "Statie Regular Droppers",
            statieRegularDroppersByDateAndStore?.[date]?.[store] ?? 0,
            "count",
            0
          );

          if (!storeDownloadsWritten.has(store)) {
            storeDownloadsWritten.add(store);
            pushRow(
              date,
              store,
              "Total Downloads",
              totalDownloadsPerStore?.[store] || 0,
              "count",
              0
            );
          }
        });
      });

      if (rows.length <= 1) {
        throw new Error(
          "No exportable rows found for the selected week range. Try another range."
        );
      }

      const csv = rows.join("\n");

      // Download CSV
      const blob = new Blob(["\uFEFF", csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `summary_export_W${exportFromWeek}_${exportFromYear}_to_W${exportToWeek}_${exportToYear}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      setAlert({
        isOpen: true,
        message: "Data exported successfully!",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to export data",
        severity: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );

  const getWeekOptions = (year: number) => {
    const weeksInYear = year === dayjs().year() ? dayjs().isoWeek() : 52;
    return Array.from({ length: weeksInYear }, (_, i) => i + 1);
  };

  return (
    <main>
      <KeyPerformanceIndicators />
      <CumulativeUsersOverTime useFromToFilters />

      {/* Data Export Button */}
      <div className="flex justify-end mb-6">
        <PrimaryButton
          label="Export Weekly Data"
          onClick={handleExportClick}
          disabled={!areChartsReady}
          className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark h-8 flex items-center justify-center"
        />
      </div>
      {!areChartsReady && (
        <p className="text-sm text-gray-500 -mt-4 mb-6 text-right">
          Export will be enabled after all charts load.
        </p>
      )}

      {/* Export Modal */}
      {isExportModalOpen && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-11/12 sm:w-2/3 lg:w-1/2 max-w-2xl my-6 mx-auto">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-gray-200 rounded-t">
                  <h3 className="text-2xl font-semibold font-satoshi text-dark">
                    Export Weekly Summary Data
                  </h3>
                  <button
                    className="text-gray-500 text-2xl leading-none"
                    onClick={() => setIsExportModalOpen(false)}
                    disabled={isExporting}
                  >
                    ×
                  </button>
                </div>

                <div className="relative p-6 flex-auto">
                  <p className="text-sm text-gray-600 mb-4">
                    Export daily data for volumes per store per material,
                    droppers per store, and regular droppers for a selected week
                    range.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div className="col-span-2">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        From
                      </h4>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year
                      </label>
                      <select
                        value={exportFromYear}
                        onChange={(e) =>
                          setExportFromYear(parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-droppie-oranje"
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Week
                      </label>
                      <select
                        value={exportFromWeek}
                        onChange={(e) =>
                          setExportFromWeek(parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-droppie-oranje"
                      >
                        {getWeekOptions(exportFromYear).map((week) => {
                          const weekStart = dayjs()
                            .year(exportFromYear)
                            .isoWeek(week)
                            .startOf("isoWeek");
                          const weekEnd = dayjs()
                            .year(exportFromYear)
                            .isoWeek(week)
                            .endOf("isoWeek");
                          return (
                            <option key={week} value={week}>
                              Week {week} ({weekStart.format("MMM D")} -{" "}
                              {weekEnd.format("MMM D")})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        To
                      </h4>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year
                      </label>
                      <select
                        value={exportToYear}
                        onChange={(e) =>
                          setExportToYear(parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-droppie-oranje"
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Week
                      </label>
                      <select
                        value={exportToWeek}
                        onChange={(e) =>
                          setExportToWeek(parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-droppie-oranje"
                      >
                        {getWeekOptions(exportToYear).map((week) => {
                          const weekStart = dayjs()
                            .year(exportToYear)
                            .isoWeek(week)
                            .startOf("isoWeek");
                          const weekEnd = dayjs()
                            .year(exportToYear)
                            .isoWeek(week)
                            .endOf("isoWeek");
                          return (
                            <option key={week} value={week}>
                              Week {week} ({weekStart.format("MMM D")} -{" "}
                              {weekEnd.format("MMM D")})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end p-6 border-t border-solid border-gray-200 rounded-b gap-3">
                  <RedButton
                    label="Cancel"
                    onClick={() => !isExporting && setIsExportModalOpen(false)}
                    className="h-8 flex items-center justify-center"
                  />
                  <PrimaryButton
                    label={isExporting ? "Exporting..." : "Export CSV"}
                    onClick={handleExport}
                    disabled={isExporting}
                    className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black" />
        </>
      )}

      {isExporting && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg px-6 py-5 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-droppie-oranje rounded-full animate-spin" />
            <span className="text-sm font-medium text-dark">
              Preparing CSV export...
            </span>
          </div>
        </div>
      )}

      <VolumesGeneralFilters
        filterType={filterType}
        setFilterType={setFilterTypeWithReset}
        filterPlaces={filterPlaces}
        setFilterPlaces={setFilterPlaces}
        sticky={false}
      />
      <Materials
        filterType={filterType}
        filterPlaces={filterPlaces}
        sequenceStep={sequenceStep}
        setSequenceStep={setSequenceStep}
        sequenceStepIndex={0}
      />
      <StatieItems
        filterType={filterType}
        filterPlaces={filterPlaces}
        sequenceStep={sequenceStep}
        setSequenceStep={setSequenceStep}
        sequenceStepIndex={1}
      />
    </main>
  );
};

export default withProtectedRoute(SummaryScreen);
